/**
 * 用户认证工具模块
 * 当前使用 sessionStorage 做纯前端存储，预留后端接口接入点
 */

import Vue from 'vue'

const AUTH_KEY = 'vue2_enterprise_auth'
const TOKEN_KEY = 'vue2_enterprise_token'

// 全局响应式认证状态，供组件绑定使用
export const authState = Vue.observable({
  user: getCurrentUser()
})

/**
 * 获取登录 Token
 * @returns {string|null}
 */
export function getToken() {
  return sessionStorage.getItem(TOKEN_KEY)
}

/**
 * 设置登录 Token
 * @param {string} token
 */
export function setToken(token) {
  sessionStorage.setItem(TOKEN_KEY, token)
}

/**
 * 移除登录 Token
 */
export function removeToken() {
  sessionStorage.removeItem(TOKEN_KEY)
}

/**
 * 获取当前登录用户信息
 * @returns {Object|null} 用户信息或null
 */
export function getCurrentUser() {
  try {
    const data = sessionStorage.getItem(AUTH_KEY)
    return data ? JSON.parse(data) : null
  } catch (e) {
    console.error('获取用户信息失败:', e)
    return null
  }
}

/**
 * 判断是否已登录
 * @returns {boolean}
 */
export function isLoggedIn() {
  return !!getCurrentUser()
}

/**
 * 保存登录状态（预留后端接口接入点）
 * TODO: 接入后端后，改为保存后端返回的 token
 * @param {Object} userInfo 用户信息
 */
export function setLogin(userInfo) {
  // 【埋点】后端接口接入处：此处应调用后端登录接口
  // const res = await api.login(credentials)
  sessionStorage.setItem(AUTH_KEY, JSON.stringify(userInfo))
  authState.user = userInfo
}

/**
 * 退出登录
 * TODO: 接入后端后，增加调用后端登出接口
 */
export function logout() {
  // 【埋点】后端接口接入处：此处应调用后端登出接口
  // await api.logout()
  sessionStorage.removeItem(AUTH_KEY)
  authState.user = null
}

/**
 * 注册用户（预留后端接口接入点）
 * TODO: 接入后端后，改为调用后端注册接口
 * @param {Object} userInfo 注册信息
 */
export function registerUser(userInfo) {
  // 【埋点】后端接口接入处：此处应调用后端注册接口
  // const res = await api.register(userInfo)

  // 纯前端模式：保存到 localStorage 模拟用户数据库
  const users = getRegisteredUsers()
  users.push(userInfo)
  localStorage.setItem('vue2_enterprise_users', JSON.stringify(users))
}

/**
 * 获取已注册用户列表（纯前端模拟用）
 * @returns {Array}
 */
export function getRegisteredUsers() {
  try {
    const data = localStorage.getItem('vue2_enterprise_users')
    return data ? JSON.parse(data) : []
  } catch (e) {
    return []
  }
}

/**
 * 验证用户登录（纯前端模拟）
 * TODO: 接入后端后，改为后端验证
 * @param {string} username 用户名
 * @param {string} password 密码
 * @returns {Object|null} 验证成功返回用户信息，失败返回null
 */
export function verifyUser(username, password) {
  // 【埋点】后端接口接入处：此处应调用后端登录验证接口
  // const res = await api.login({ username, password })

  const users = getRegisteredUsers()
  const user = users.find(u => u.username === username && u.password === password)
  return user || null
}

/**
 * 检查用户名是否已存在
 * @param {string} username 用户名
 * @returns {boolean}
 */
export function isUsernameExists(username) {
  const users = getRegisteredUsers()
  return users.some(u => u.username === username)
}

/**
 * 生成验证码（纯前端图形验证码）
 * @param {HTMLCanvasElement} canvas 画布元素
 * @param {number} width 宽度
 * @param {number} height 高度
 * @returns {string} 生成的验证码文本
 */
export function generateCaptcha(canvas, width = 120, height = 40) {
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, width, height)

  // 背景色
  ctx.fillStyle = '#f0f2f5'
  ctx.fillRect(0, 0, width, height)

  // 生成随机验证码（4位字母数字）
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  let captcha = ''
  for (let i = 0; i < 4; i++) {
    captcha += chars.charAt(Math.floor(Math.random() * chars.length))
  }

  // 绘制干扰线
  for (let i = 0; i < 5; i++) {
    ctx.strokeStyle = `rgb(${Math.random() * 255},${Math.random() * 255},${Math.random() * 255})`
    ctx.beginPath()
    ctx.moveTo(Math.random() * width, Math.random() * height)
    ctx.lineTo(Math.random() * width, Math.random() * height)
    ctx.stroke()
  }

  // 绘制文字
  for (let i = 0; i < captcha.length; i++) {
    ctx.font = `bold ${20 + Math.random() * 8}px Arial`
    ctx.fillStyle = `rgb(${Math.random() * 100},${Math.random() * 100},${Math.random() * 100})`
    ctx.textBaseline = 'middle'
    const angle = (Math.random() - 0.5) * 0.4
    ctx.save()
    ctx.translate(20 + i * 25, height / 2)
    ctx.rotate(angle)
    ctx.fillText(captcha[i], 0, 0)
    ctx.restore()
  }

  // 绘制干扰点
  for (let i = 0; i < 30; i++) {
    ctx.fillStyle = `rgb(${Math.random() * 255},${Math.random() * 255},${Math.random() * 255})`
    ctx.beginPath()
    ctx.arc(Math.random() * width, Math.random() * height, 1, 0, 2 * Math.PI)
    ctx.fill()
  }

  return captcha
}

/**
 * 验证用户名格式
 * 最多4个汉字或12个英文字母（含数字）
 * @param {string} username
 * @returns {boolean}
 */
export function validateUsername(username) {
  if (!username) return false
  // 纯中文 1-4 位，或纯英文/数字 1-12 位
  const reg = /^(?:[一-龥]{1,4}|[a-zA-Z0-9]{1,12})$/
  return reg.test(username)
}

/**
 * 验证密码格式（至少6位，建议包含字母和数字）
 * @param {string} password
 * @returns {boolean}
 */
export function validatePassword(password) {
  return typeof password === 'string' && password.length >= 6 && password.length <= 20
}

/**
 * 验证手机号格式
 * @param {string} mobile
 * @returns {boolean}
 */
export function validateMobile(mobile) {
  const reg = /^1[3-9]\d{9}$/
  return reg.test(mobile)
}
